import React, {useState} from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {useDangerouslySelectTemplateCollectionTemplateListUnfiltered} from 'notebook/entry-templates/SelectTemplateCollectionItemTree/SelectTemplateCollectionItemTree';
import Modal from 'ui-library/modals/Modal';
import LegacyTagsInput from 'ui-library/tags-input/LegacyTagsInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

import {
  StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates as EntryTemplateIconOrInaccessibleInfo,
  StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates_InaccessibleInfoNode as InaccessibleInfoNode,
} from './__generated/StudySchemaQuery';

interface SetEntryTemplatesModalOnStudySchemasProps {
  setEntryTemplates: (entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>) => void;
  selectedTemplates: Array<EntryTemplateIconOrInaccessibleInfo>;
  onRequestClose: () => void;
}

export default function SetEntryTemplatesModalOnStudySchemas({
  setEntryTemplates,
  selectedTemplates,
  onRequestClose,
}: SetEntryTemplatesModalOnStudySchemasProps): JSX.Element {
  const allTemplates = useDangerouslySelectTemplateCollectionTemplateListUnfiltered().map((template) => ({
    id: template.id,
    text: template.name,
  }));
  const inaccessibleTemplates = selectedTemplates.filter((template) => isInaccessible(template));
  const inaccessibleTemplateIds = new Set(
    inaccessibleTemplates.map((template: InaccessibleInfoNode) => template.invalidId)
  );
  const [userSelection, setUserSelection] =
    useState<Array<EntryTemplateIconOrInaccessibleInfo>>(selectedTemplates);
  const handleSubmitEntryTemplatesModal: () => void = () => {
    setEntryTemplates(userSelection);
    onRequestClose();
  };

  function handleOnChange(options: Array<{id: string; text: string}>): void {
    const templates: Array<EntryTemplateIconOrInaccessibleInfo> = options.map((option) => {
      if (inaccessibleTemplateIds?.has(option.id)) {
        return {__typename: 'InaccessibleInfoNode', invalidId: option.id, canRequestAccess: false};
      } else {
        return {__typename: 'TemplateNode', id: option.id, name: option.text};
      }
    });
    setUserSelection(templates);
  }

  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmitEntryTemplatesModal}
      title="Add Entry Templates"
      buttons={[
        {
          text: 'Done',
          submit: true,
        },
      ]}
      // scrollable={false} and expandHeight={false} ensure that the template selector appears on top of the modal
      scrollable={false}
      expandHeight={false}
    >
      <LegacyTagsInput
        allOptions={allTemplates}
        allowAdding={true}
        onChange={(options: Array<{id: string; text: string}>) => {
          handleOnChange(options);
        }}
        placeholder="Set Entry Templates"
        size={TagsInputSize.MEDIUM}
        value={userSelection.map((template) => {
          if (!isInaccessible(template)) {
            return {id: template.id, text: template.name};
          } else {
            return {id: template.invalidId, text: 'No access'};
          }
        })}
      />
    </Modal>
  );
}
